import React, { useEffect, useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { usePackagingPurchaseOrderItems } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderQueries';
import { PackagingPOStatusCell } from 'components/packaging/components/packagingPOV2/PackagingPOStatusCell';
import { normalizePackagingItems } from 'components/packaging/components/packagingPOV2/utils/normalizePackagingItems';
import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Badge } from 'shadcn/components/ui/Badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

const EMPTY_ITEMS = [];

function DescriptionCell({ getValue }) {
    const value = normalizeOptionalText(getValue());
    if (value === FALLBACK_TEXT) return <span className="text-left">{value}</span>;

    return (
        <span className="block max-w-[300px] truncate text-left" title={value}>
            {value}
        </span>
    );
}

function formatFcDeliveryLocation(headerSource) {
    const facilityName = headerSource?.facilityName ?? '';
    const shipToCrossDockName = headerSource?.shipToCrossDockName ?? null;
    if (shipToCrossDockName) {
        return `${facilityName || FALLBACK_TEXT} / ${shipToCrossDockName}`;
    }
    return facilityName || FALLBACK_TEXT;
}

/**
 * Read-only modal showing partner packaging purchase order line items.
 *
 * @param {Object} props
 * @param {Object|null} props.purchaseOrder - List-row purchase order (header fields + id for items fetch)
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 */
export function ViewPartnerPackagingPurchaseOrderLineItemsModal({ onOpenChange, open, purchaseOrder }) {
    const purchaseOrderId = purchaseOrder?.id;

    const {
        data: detail,
        error,
        isFetching,
        isLoading,
    } = usePackagingPurchaseOrderItems({
        id: purchaseOrderId,
        enabled: open && Boolean(purchaseOrderId),
    });

    useEffect(() => {
        if (open) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => {
            document.body.style.pointerEvents = '';
        }, 0);
        return () => window.clearTimeout(timeoutId);
    }, [open]);

    const headerSource = purchaseOrder ?? null;
    const rawItems = detail?.items ?? EMPTY_ITEMS;
    const items = useMemo(() => normalizePackagingItems(rawItems), [rawItems]);

    const headerFields = useMemo(() => {
        if (headerSource == null) return [];

        const fields = [
            { key: 'id', label: 'PO #', value: normalizeOptionalText(headerSource.id) },
            {
                key: 'status',
                label: null,
                value: headerSource.status != null ? normalizeOptionalText(headerSource.status) : FALLBACK_TEXT,
                render: (value) => <PackagingPOStatusCell status={value} fallbackText={FALLBACK_TEXT} />,
            },
            { key: 'vendor', label: 'Vendor', value: normalizeOptionalText(headerSource.vendorName) },
            {
                key: 'delivery',
                label: 'Scheduled Delivery Date',
                value: formatPurchaseOrderDate(headerSource.scheduledDeliveryDate, FALLBACK_TEXT),
            },
            {
                key: 'fcDeliveryLocation',
                label: 'FC/Delivery Location',
                value: formatFcDeliveryLocation(headerSource),
            },
            {
                key: 'reference',
                label: 'Reference #',
                value: normalizeOptionalText(headerSource.referenceNumber ?? headerSource.reference),
            },
        ];

        return fields.filter((field) => field.value !== FALLBACK_TEXT);
    }, [headerSource]);

    const columns = useMemo(
        () => [
            {
                id: 'masterSku',
                header: 'Item Master SKU',
                accessorFn: (row) => row?.masterSku ?? row?.sku ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'description',
                header: 'Description',
                accessorFn: (row) => row?.description ?? row?.itemName ?? null,
                cell: DescriptionCell,
            },
            {
                id: 'totalUnits',
                header: 'Total Units',
                accessorFn: (row) => {
                    const qtyRaw = row?.qty;
                    const unitsPerCaseRaw = row?.unitsPerCase;
                    if (qtyRaw == null || unitsPerCaseRaw == null) return null;
                    const qty = Number(qtyRaw);
                    const unitsPerCase = Number(unitsPerCaseRaw);
                    return Number.isFinite(qty) && Number.isFinite(unitsPerCase) ? qty * unitsPerCase : null;
                },
                cell: ({ getValue }) => {
                    const total = getValue();
                    return total != null ? formatNumber(total) : '';
                },
            },
        ],
        [],
    );

    const table = useReactTable({
        data: items,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const showItemsLoading = open && Boolean(purchaseOrderId) && (isLoading || (isFetching && !detail));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-3xl">
                <DialogHeader className="shrink-0">
                    <DialogTitle>Packaging Purchase Order Items</DialogTitle>
                    <DialogDescription className="sr-only">
                        Read-only line items for the selected packaging purchase order.
                    </DialogDescription>
                    {headerFields.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
                            {headerFields.map((field) =>
                                field.render ? (
                                    <React.Fragment key={field.key}>{field.render(field.value)}</React.Fragment>
                                ) : (
                                    <span key={field.key} className="text-sm text-muted-foreground">
                                        {field.label}:{' '}
                                        <span className="font-medium text-foreground">{field.value}</span>
                                    </span>
                                ),
                            )}
                        </div>
                    )}
                </DialogHeader>

                {error ? (
                    <div className="mt-4 shrink-0 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                        {error.message || 'Failed to load packaging purchase order items.'}
                    </div>
                ) : null}

                {showItemsLoading ? (
                    <div className="mt-6 flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Loading line items…
                    </div>
                ) : (
                    <div
                        data-testid="partner-packaging-po-line-items-scroll"
                        className="mt-4 min-h-0 flex-1 overflow-y-auto"
                    >
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id} className="text-left">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            No line items found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {items.length > 0 && (
                            <div className="mt-2">
                                <Badge variant="outline" className="text-xs">
                                    {items.length} {items.length === 1 ? 'item' : 'items'}
                                </Badge>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
